import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, FileText } from "lucide-react";
import {
  createPodcast,
  getPodcast,
  useGetPodcast,
  getGetUserPodcastsQueryKey,
} from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import PodcastAudioCard from "./PodcastAudioCard";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { useToast } from "@packages/contexts/ToastContext";
import { Button, Text, CEFRSelector } from "@core/index";
import {
  CreatePodcastFromReferenceRequestDtoGenerationType,
  CreatePodcastRequestDtoContentLanguage,
  CreatePodcastRequestDtoGenerationType,
  CreatePodcastRequestDtoCefrLevel,
  PodcastResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { FileUploader } from "@core/file-uploader";
import LoadingContainer from "@packages/components/Loading";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";

interface Question {
  questionText: string;
  questionAnswer: string;
}

export const exampleTopics = {
  tr: [
    "Dinozorların Gizemli Dünyası",
    "Gökyüzündeki Devler: Gezegenler ve Uydular",
    "Işık ve Optik: Gözlerimiz Nasıl Görür?",
    "Rüzgarın Gücü: Fırtınalar ve Kasırgalar",
    "Yeraltındaki Yaşam: Mağaralar ve Depremler",
    "Elektrik ve Manyetizma",
    "Buzullar ve Kutup Bölgeleri",
    "Ses Dalgaları: Müzik ve Fizik",
    "Mikroskobik Dünya: Hücreler ve DNA",
    "Bitkiler Nasıl Büyür? Fotosentezin Sırları",
    "İnsan Vücudu: Organlarımızın Çalışma Prensibi",
    "Yıldızların Doğumu ve Ölümü",
    "Zamanın Akışı: Saatler ve Takvimler",
    "Derin Deniz Keşifleri",
    "Mimari ve Mühendislik: Köprüler Nasıl Yapılır?",
    "Matematik Dünyası: Sayılar ve Geometri",
    "Robotların Yükselişi",
    "Hayvanların Hayatta Kalma Stratejileri",
    "İnsan Beyni: Zeka ve Hafıza",
    "Enerji Kaynakları: Yenilenebilir ve Fosil Yakıtlar",
    "Yer Altındaki Hazine: Mineraller ve Madenler",
    "Göktaşları ve Kuyruklu Yıldızlar",
    "Renklerin Bilimi: Pigmentler ve Işık Tayfı",
    "Zehirli Canlılar: Hayatta Kalma Mekanizmaları",
    "Gizemli Antik Uygarlıklar",
    "Volkanlar ve Lav Nehirleri",
    "Hava Durumu Nasıl Tahmin Edilir?",
    "Uzak Geçmişten Günümüze Evrim",
    "Tarihte Çığır Açan Kadın Bilim İnsanları",
    "Su Döngüsü ve Yaşam İçin Önemi",
    "İnsanların İlk Keşif Yolculukları",
    "Görünmeyen Güç: Atom ve Moleküller",
    "Zehirli Bitkiler ve Kullanım Alanları",
    "Kuyular ve Su Kaynakları",
    "Matematikte Sonsuzluk Kavramı",
    "İnsan Vücudundaki Mikroplar",
    "Süper Hızlı Hayvanlar: Çita ve Diğerleri",
    "Uçan Canlılar: Kuşlar, Yarasa ve Böcekler",
    "Yüzey Gerilimi ve Sıvıların Davranışı",
    "Denizlerin Derinliklerinde Yaşam",
    "Köpekbalıkları: Evrim ve Ekoloji",
    "Gizli Evren: Karanlık Madde ve Karanlık Enerji",
    "Makineler Nasıl Çalışır?",
    "Çizgi Romanlardaki Süper Güçlerin Gerçekliği",
    "Düşüncenin Gücü: Zihin ve Nörobilim",
    "Yerçekimsiz Ortamda Deneyler",
    "Yapay Zeka ve Gelecek Teknolojileri",
    "Denizanası ve Biyolüminesans",
    "Sosyal Böcekler: Karıncalar ve Arılar",
    "Elektronik Aletler Nasıl Çalışır?",
    "Tarihte Büyük Depremler ve Etkileri",
    "Hayalet Parçacıklar: Nötrinolar",
    "Dünya'nın Katmanları ve Levha Tektoniği",
    "İlk Bilgisayarlar ve Günümüz Teknolojisi",
    "Hayvanların Haberleşme Yöntemleri",
    "Büyük Sayılar: Googleplex Nedir?",
    "Hızlı Trenlerin Teknolojisi",
    "Ses Dalgalarının Kullanım Alanları",
    "Fizikte Kuantum Teorisi",
    "Göz Yanılmaları ve Algı",
    "Zehirli Mantarlar ve Doğadaki Rolü",
    "İnsanın Evrimi ve Atalarımız",
    "Tarihteki En Ünlü Materyaller: Altın, Elmas ve Uranyum",
    "Böceklerin Ekosisteme Katkısı",
    "Astronotların Günlük Hayatı",
    "Dünya'nın Manyetik Alanı ve Pusula",
    "Sesin Sıcaklık ve Ortama Göre Yayılması",
    "Ay'ın Evreleri ve Gelgit Olayları",
    "Alternatif Tıp ve Bitkisel Tedaviler",
    "Mars'ta Yaşam Mümkün mü?",
    "Antik Çin Buluşları ve Bilimi",
    "Hayvanlarda Kamuflaj ve Adaptasyon",
    "DNA'nın Keşfi ve Genetik Mühendisliği",
    "Yıldırımların Bilimi",
    "Nanoteknoloji: Küçük Ama Güçlü",
    "Dalgaların Gücü: Deniz ve Ses Dalgaları",
    "İnsanlığın İlk Yazılı Dilleri",
    "Rüzgar Türbinleri ve Yeşil Enerji",
    "Sesin Uzayda Yayılmaması",
    "Mumya Bilimi: Antik Mısır ve Mumyalama Teknikleri",
    "Antik Roma'nın Su Mühendisliği",
    "Zaman Yolculuğu: Bilim Kurgu mu Gerçek mi?",
    "Denizaltılar ve Batık Gemiler",
    "İnsan Duyuları ve Beyin Algısı",
    "Tarımdaki Devrimler ve Modern Teknoloji",
    "Sineklerin Görme Yetisi",
    "İnternetin Anatomisi: Veri Nasıl Akıyor?",
    "Güçlü Malzemeler: Karbon Fiber ve Grafen",
    "İnsanların İlk Evleri ve Yerleşimleri",
    "Duyguların Nörolojik Temelleri",
    "Karınca Kolonilerinin Organizasyonu",
    "Deniz Tuzu ve Okyanus Akıntıları",
    "İlk Teleskoplardan Günümüz Uzay Teleskoplarına",
    "Beyin ve Yapay Zeka Arasındaki Benzerlikler",
    "Deprem Dalgaları ve Sismoloji",
    "Çölde Hayatta Kalma Yöntemleri",
    "Derin Denizdeki Volkanlar ve Termal Bacalar",
    "Bilimkurgu Filmlerindeki Bilimsel Gerçeklik",
    "Uzay İstasyonlarının Çalışma Mekanizması",
    "Gezegenlerin Manyetik Alanları ve Koruyucu Etkileri",
    "Antik Yunan Filozofları ve Düşünce Akımları",
    "Osmanlı'nın Yükselişi ve Fetihler",
    "Rönesans'ın Sanat ve Bilime Katkıları",
    "Endüstri Devrimi: Makinaların Yükselişi",
    "Vikinglerin Keşif Yolculukları",
    "Antik Roma'da Günlük Yaşam",
    "İpek Yolu ve Kültürel Etkileşim",
    "Tarihteki En Büyük Savaşlar",
    "Eski Mısır'ın Tıp Bilimi",
    "Amerikan Bağımsızlık Savaşı'nın Sonuçları",
    "Hititler: Anadolu'nun Gizemli Krallığı",
    "Büyük İskender ve Kültürel Mirası",
    "Gutenberg ve Matbaanın İcadı",
    "Napolyon'un Avrupa'yı Değiştiren Seferleri",
    "İlk Kadın Hakları Hareketleri ve Feminist Tarih",
    "Moğol İmparatorluğu'nun Yönetim Sistemi",
    "İnka ve Maya Medeniyetleri",
    "Haçlı Seferleri: Nedenleri ve Sonuçları",
    "Sanayi Devrimi ve İşçi Hakları",
    "Soğuk Savaş: ABD ve SSCB Arasındaki Rekabet",
    "İkinci Dünya Savaşı'nın Bilim ve Teknolojiye Etkileri",
    "Osmanlı'da Eğitim ve Medrese Kültürü",
    "Kara Ölüm: Orta Çağ Avrupa'sında Veba Salgını",
    "Afrika Krallıkları: Mali, Songhai ve Benin",
    "Antik Mezopotamya ve İlk Yazı Sistemleri",
    "Altın Oran ve Sanattaki Kullanımı",
    "Kaos Teorisi ve Kelebek Etkisi",
    "Matematikte Sonsuzluk Paradoksları",
    "Euler'in Sayısı ve Doğadaki Yeri",
    "Geometri ve Doğadaki Şekiller",
    "Fraktaller ve Kendini Tekrarlayan Desenler",
    "İlk Bilgisayar Algoritmaları ve Alan Turing",
    "Sayı Teorisi ve Asal Sayılar",
    "İstatistik ve Olasılık Teorisi",
    "Diferansiyel Denklemler ve Fizikteki Önemi",
    "Pi Sayısının Keşfi ve Hesaplanması",
    "Tarih Boyunca Şifreleme Yöntemleri",
    "Matematikte Fermat'ın Son Teoremi",
    "Üçgenler ve Trigonometri'nin Gelişimi",
    "Venn Diyagramları ve Mantık Sistemleri",
    "İrrasyonel Sayılar ve Gerçek Dünya Kullanımı",
    "Büyük Sayılar: Googleplex ve Ötesi",
    "Matematikte Oyun Teorisi ve Strateji",
    "Matematik ve Kriptografi",
    "Türev ve İntegral: Kalkülüsün Doğuşu",
    "Elementlerin Keşfi ve Periyodik Tablo",
    "Kimyanın Günlük Hayattaki Yeri",
    "Radyoaktivitenin Keşfi ve Kullanımı",
    "Sıvılar ve Gazlar: Basınç ve Hareket",
    "Plastik ve Çevresel Etkileri",
    "Asitler ve Bazlar: pH Dengesi",
    "Endotermik ve Ekzotermik Tepkimeler",
    "Kimyasal Bağlar ve Maddenin Yapısı",
    "DNA ve Genetik Kimya",
    "Elementlerin İzotopları ve Kullanım Alanları",
    "Kimyasal Tepkimelerde Enerji ve Isı",
    "Polimerler ve Günlük Hayattaki Yeri",
    "Fosfor ve Patlayıcı Kimyasallar",
    "Organik Kimya ve Karbon Bileşikleri",
    "Metalurji ve Tarih Boyunca Metal Kullanımı",
    "Boya Pigmentleri ve Kimyası",
    "Antibiyotiklerin Keşfi ve Kimyası",
    "Kimyasal Reaksiyonların Hızını Etkileyen Faktörler",
    "Atmosferdeki Kimyasal Olaylar",
    "Gıda Kimyası: Yediğimiz Şeylerin İçeriği",
    "Kimyada Nobel Ödülleri ve Büyük Buluşlar",
    "Hidrojen Bağı ve Hayati Önemi",
    "Kimyanın Tıpta Kullanımı",
    "İlk Sentetik Kimyasal Bileşikler",
    "Sabun ve Deterjanların Kimyası",
    "Antik Destanlar: Gılgamış, İlyada ve Mahabharata",
    "Shakespeare'in Eserlerindeki Dramatik Yapı",
    "Dostoyevski ve İnsan Psikolojisi",
    "Modern Romanın Doğuşu",
    "Divan Edebiyatı ve Kaside Sanatı",
    "Şiirde Ölçü ve Kafiye Düzeni",
    "Türk Halk Hikayeleri ve Efsaneleri",
    "Gerçeküstücü Edebiyatın Doğuşu",
    "Gotik Romanlar ve Korku Edebiyatı",
    "Bilim Kurgu Edebiyatı ve Gelecek Kurguları",
    "Epik Şiirin Tarihsel Evrimi",
    "Franz Kafka ve Absürdizm",
    "Latin Amerika Edebiyatında Büyülü Gerçekçilik",
    "Tanzimat Edebiyatı ve İlk Romanlarımız",
    "Distopya Romanları ve Toplum Eleştirisi",
    "Dünya Mitolojilerinde Kahramanlar",
    "Postmodern Roman ve Anlatı Teknikleri",
    "İslam Edebiyatında Mesnevi ve Hikayeler",
    "Klasik Dönem Rus Edebiyatı",
    "İlk Basılı Kitap ve Yayıncılığın Tarihi",
    "Masalların Kökenleri ve Evrimi",
    "Sembolist Şiir ve Dilin Kullanımı",
    "Büyük Fransız Romancıları: Hugo, Balzac, Flaubert",
    "Kurmaca ve Gerçek Arasındaki Sınır",
    "Edebiyatta Melankoli ve Mutsuzluk Temaları",
    "Orta Çağ'da Yazılmış Efsanevi Kitaplar",
    "Savaş Romanları ve Toplumsal Bellek",
    "Çağdaş Türk Edebiyatında Yeni Akımlar",
    "Öykü Yazma Teknikleri ve Kurallar",
    "Edebi Üslup ve Yazarın Kimliği",
  ],
  en: [
    "The Mysterious World of Dinosaurs",
    "Giants in the Sky: Planets and Moons",
    "Light and Optics: How Do Our Eyes See?",
    "The Power of Wind: Storms and Hurricanes",
    "Life Underground: Caves and Earthquakes",
    "Electricity and Magnetism",
    "Glaciers and Polar Regions",
    "Sound Waves: Music and Physics",
    "The Microscopic World: Cells and DNA",
    "How Do Plants Grow? The Secrets of Photosynthesis",
    "The Human Body: How Our Organs Work",
    "The Birth and Death of Stars",
    "The Flow of Time: Clocks and Calendars",
    "Deep-Sea Explorations",
    "Architecture and Engineering: How Are Bridges Built?",
    "The World of Mathematics: Numbers and Geometry",
    "The Rise of Robots",
    "Animals' Survival Strategies",
    "The Human Brain: Intelligence and Memory",
    "Energy Sources: Renewable vs. Fossil Fuels",
    "The Treasure Underground: Minerals and Mines",
    "Meteors and Comets",
    "The Science of Colors: Pigments and Light Spectrum",
    "Poisonous Creatures: Survival Mechanisms",
    "Mysterious Ancient Civilizations",
    "Volcanoes and Rivers of Lava",
    "How is Weather Predicted?",
    "Evolution: From the Distant Past to Today",
    "Pioneering Women in Science",
    "The Water Cycle and Its Importance for Life",
    "Humanity's First Exploration Journeys",
    "The Invisible Force: Atoms and Molecules",
    "Toxic Plants and Their Uses",
    "Wells and Water Sources",
    "The Concept of Infinity in Mathematics",
    "Microbes in the Human Body",
    "Super-Fast Animals: The Cheetah and Others",
    "Flying Creatures: Birds, Bats, and Insects",
    "Surface Tension and the Behavior of Liquids",
    "Life in the Deep Oceans",
    "Sharks: Evolution and Ecology",
    "The Hidden Universe: Dark Matter and Dark Energy",
    "How Do Machines Work?",
    "Superpowers in Comic Books: Science or Fiction?",
    "The Power of Thought: Mind and Neuroscience",
    "Experiments in Zero Gravity",
    "Artificial Intelligence and Future Technologies",
    "Jellyfish and Bioluminescence",
    "Social Insects: Ants and Bees",
    "How Do Electronic Devices Work?",
    "The Biggest Earthquakes in History and Their Effects",
    "Ghost Particles: Neutrinos",
    "The Earth's Layers and Plate Tectonics",
    "The First Computers and Today's Technology",
    "How Do Animals Communicate?",
    "Huge Numbers: What is a Googolplex?",
    "High-Speed Train Technology",
    "The Applications of Sound Waves",
    "Quantum Theory in Physics",
    "Optical Illusions and Perception",
    "Poisonous Mushrooms and Their Role in Nature",
    "Human Evolution and Our Ancestors",
    "The Most Famous Materials in History: Gold, Diamonds, and Uranium",
    "The Role of Insects in Ecosystems",
    "Daily Life of an Astronaut",
    "The Earth's Magnetic Field and the Compass",
    "How Sound Travels in Different Temperatures and Environments",
    "The Moon's Phases and the Tides",
    "Alternative Medicine and Herbal Treatments",
    "Is Life on Mars Possible?",
    "Ancient Chinese Inventions and Science",
    "Camouflage and Adaptation in Animals",
    "The Discovery of DNA and Genetic Engineering",
    "The Science of Lightning",
    "Nanotechnology: Small but Powerful",
    "The Power of Waves: Ocean and Sound Waves",
    "The First Written Languages of Humanity",
    "Wind Turbines and Green Energy",
    "Why Sound Doesn't Travel in Space",
    "Mummy Science: Ancient Egypt and Mummification Techniques",
    "Ancient Roman Water Engineering",
    "Time Travel: Science Fiction or Reality?",
    "Submarines and Sunken Ships",
    "Human Senses and Brain Perception",
    "Agricultural Revolutions and Modern Technology",
    "How Do Flies See?",
    "The Anatomy of the Internet: How Does Data Flow?",
    "Powerful Materials: Carbon Fiber and Graphene",
    "The First Human Homes and Settlements",
    "The Neurological Basis of Emotions",
    "Ant Colonies and Their Organization",
    "Sea Salt and Ocean Currents",
    "From the First Telescopes to Today's Space Telescopes",
    "The Similarities Between the Brain and Artificial Intelligence",
    "Seismic Waves and Earthquake Science",
    "Survival Strategies in the Desert",
    "Deep-Sea Volcanoes and Hydrothermal Vents",
    "The Scientific Accuracy of Science Fiction Movies",
    "How Do Space Stations Work?",
    "Magnetic Fields of Planets and Their Protective Effects",
  ],
  de: [
    "Die Geheimnisvolle Welt der Dinosaurier",
    "Riesen am Himmel : Planeten und Monde",
    "Licht und Optik : Wie sehen unsere Augen?",
    "Die Macht des Windes : Stürme und Hurrikane",
    "Leben unter der Erde : Höhlen und Erdbeben",
    "Elektrizität und Magnetismus",
    "Gletscher und Polarregionen",
    "Schallwellen : Musik und Physik",
    "Die Mikroskopische Welt : Zellen und DNA",
    "Wie wachsen Pflanzen ? Die Geheimnisse der Photosynthese",
    "Der Menschliche Körper : Wie unsere Organe arbeiten",
    "Geburt und Tod der Sterne",
    "Der Fluss der Zeit : Uhren und Kalender",
    "Tiefsee-Erkundungen",
    "Architektur und Ingenieurwesen : Wie werden Brücken gebaut?",
    "Die Welt der Mathematik : Zahlen und Geometrie",
    "Der Aufstieg der Roboter",
    "Überlebensstrategien der Tiere",
    "Das Menschliche Gehirn : Intelligenz und Gedächtnis",
    "Energiequellen : Erneuerbare vs. Fossile Brennstoffe",
    "Der Schatz unter der Erde : Mineralien und Minen",
    "Meteore und Kometen",
    "Die Wissenschaft der Farben : Pigmente und Lichtspektrum",
    "Giftige Kreaturen : Überlebensmechanismen",
    "Geheimnisvolle Antike Zivilisationen",
    "Vulkane und Lavaflüsse",
    "Wie wird das Wetter vorhergesagt?",
    "Evolution : Von der fernen Vergangenheit bis heute",
    "Pionierinnen de la science",
    "Der Wasserkreislauf und seine Bedeutung für das Leben",
    "Die ersten Entdeckungsreisen der Menschheit",
    "Die Unsichtbare Kraft : Atome und Moleküle",
    "Giftige Pflanzen und ihre Verwendung",
    "Brunnen und Wasserquellen",
    "Das Konzept der Unendlichkeit in der Mathematik",
    "Mikroben im menschlichen Körper",
    "Superschnelle Tiere : Der Gepard und andere",
    "Fliegende Kreaturen : Vögel, Fledermäuse und Insekten",
    "Oberflächenspannung und Verhalten von Flüssigkeiten",
    "Leben in den tiefen Ozeanen",
    "Haie : Evolution und Ökologie",
    "Das Verborgene Universum : Dunkle Materie und Dunkle Energie",
    "Wie funktionieren Maschinen?",
    "Superkräfte in Comics : Wissenschaft oder Fiktion?",
    "Die Macht des Denkens : Geist und Neurowissenschaft",
    "Experimente in der Schwerelosigkeit",
    "Künstliche Intelligenz und Zukunftstechnologien",
    "Quallen und Biolumineszenz",
    "Soziale Insekten : Ameisen und Bienen",
    "Wie funktionieren elektronische Geräte?",
    "Die größten Erdbeben der Geschichte und ihre Auswirkungen",
    "Geisterpartikel : Neutrinos",
    "Die Schichten der Erde und Plattentektonik",
    "Die ersten Computer und heutige Technologie",
    "Wie kommunizieren Tiere?",
    "Riesige Zahlen : Was ist ein Googolplex?",
    "Hochgeschwindigkeits-Zugtechnologie",
    "Anwendungen von Schallwellen",
    "Quantentheorie in der Physik",
    "Starke Materialien: Kohlefaser und Graphen",
    "Die ersten menschlichen Häuser und Siedlungen",
    "Die neurologische Basis von Emotionen",
    "Ameisenkolonien und ihre Organisation",
    "Meersalz und Meeresströmungen",
    "Von den ersten Teleskopen zu heutigen Weltraumteleskopen",
    "Die Ähnlichkeiten zwischen Gehirn und künstlicher Intelligenz",
    "Seismische Wellen und Erdbebenwissenschaft",
    "Überlebensstrategien in der Wüste",
    "Tiefseevulkane und hydrothermale Quellen",
    "Die wissenschaftliche Genauigkeit von Science-Fiction-Filmen",
  ],
  fr: [
    "Comment fonctionnent les stations spatiales ?",
    "Champs magnétiques des planètes et effets protecteurs",
    "Philosophes grecs antiques et écoles de pensée",
    "L’essor de l’Empire ottoman et ses conquêtes",
    "Contributions de la Renaissance à l’art et à la science",
    "La révolution industrielle : l’essor des machines",
    "Les explorations vikings",
    "La vie quotidienne dans la Rome antique",
    "La route de la soie et les échanges culturels",
    "Les plus grandes guerres de l’histoire",
    "La médecine de l’Égypte ancienne",
    "Conséquences de la guerre d’indépendance américaine",
    "Les Hittites : le mystérieux royaume d’Anatolie",
    "Alexandre le Grand et son héritage culturel",
    "Gutenberg et l’invention de l’imprimerie",
    "Les campagnes de Napoléon qui ont changé l’Europe",
    "Les premiers mouvements féministes et l’histoire des droits des femmes",
    "Le système administratif de l’Empire mongol",
    "Civilisations inca et maya",
    "Les croisades : causes et conséquences",
    "La révolution industrielle et les droits des travailleurs",
    "La guerre froide : rivalité entre les USA et l’URSS",
    "Conséquences de la Seconde Guerre mondiale sur la science et la technologie",
    "Éducation et culture des médersas dans l’Empire ottoman",
    "La peste noire : pandémie médiévale en Europe",
    "Royaumes africains : Mali, Songhaï et Bénin",
    "La Mésopotamie antique et les premiers systèmes d’écriture",
    "La divine proportion et son usage dans l’art",
    "Théorie du chaos et effet papillon",
    "Paradoxes de l’infini en mathématiques",
    "Le nombre d’Euler et sa place dans la nature",
    "Géométrie et formes dans la nature",
    "Fractales et motifs répétitifs",
    "Les premiers algorithmes informatiques et Alan Turing",
    "Théorie des nombres et nombres premiers",
    "Statistiques et probabilités",
    "Équations différentielles et leur importance en physique",
    "La découverte et le calcul de Pi",
    "Verschlüsselungsmethoden durch die Geschichte",
    "Le dernier théorème de Fermat",
    "Triangles et développement de la trigonométrie",
    "Diagrammes de Venn et systèmes logiques",
    "Nombres irrationnels et leur application dans le monde réel",
    "Grands nombres : googolplex et au-delà",
    "Théorie des jeux et stratégie en mathématiques",
    "Mathématiques et cryptographie",
    "Dérivée et intégrale : la naissance du calcul infinitésimal",
    "La découverte des éléments et le tableau périodique",
    "La chimie au quotidien",
    "La découverte et l’utilisation de la radioactivité",
    "Liquides et gaz : pression et mouvement",
    "Plastique et son impact environnemental",
    "Acides et bases : équilibre du pH",
    "Réactions endothermiques et exothermiques",
    "Liaisons chimiques et structure de la matière",
    "ADN et chimie génétique",
    "Isotopes des éléments et leurs applications",
    "Énergie et chaleur dans les réactions chimiques",
    "Polymères et leur rôle dans la vie quotidienne",
    "Phosphore et produits chimiques explosifs",
    "Chimie organique et composés du carbone",
    "Métallurgie et utilisation des métaux à travers l’histoire",
    "Pigments colorés et leur chimie",
    "La découverte des antibiotiques et leur chimie",
    "Facteurs influençant la vitesse des réactions chimiques",
    "Phénomènes chimiques dans l’atmosphère",
    "Chimie alimentaire : ce que nous mangeons",
    "Prix Nobel de chimie et grandes découvertes",
    "Liaisons hydrogène et leur importance vitale",
    "L’utilisation de la chimie en médecine",
    "Les premiers composés chimiques synthétiques",
    "La chimie du savon et des détergents",
    "Épopées antiques : Gilgamesh, Iliade et Mahabharata",
    "Structure dramatique dans les œuvres de Shakespeare",
    "Dostoïevski et la psychologie humaine",
    "La naissance du roman moderne",
    "Littérature divan et l’art du qasida",
    "Mètre et schéma de rimes en poésie",
    "Contes et légendes populaires turcs",
    "La naissance de la littérature surréaliste",
    "Romans gothiques et littérature d’horreur",
    "Littérature de science-fiction et visions du futur",
    "L’évolution historique de l’épopée",
    "Franz Kafka et l’absurdisme",
    "Réalisme magique dans la littérature latino-américaine",
    "Littérature de Tanzimat et nos premiers romans",
    "Romans dystopiques et critique sociale",
    "Héros dans les mythologies du monde",
    "Romans postmodernes et techniques narratives",
    "Mesnevi et récits dans la littérature islamique",
    "Littérature russe de la période classique",
    "Le premier livre imprimé et l’histoire de l’édition",
    "Origines et évolution des contes de fées",
    "Poésie symboliste et usage du langage",
    "Grands romanciers français : Hugo, Balzac, Flaubert",
    "La frontière entre fiction et réalité",
    "Mélancolie et malheur comme thèmes littéraires",
    "Livres légendaires du Moyen Âge",
    "Romans de guerre et mémoire collective",
    "Nouvelles tendances dans la littérature turque contemporaine",
    "Techniques et règles de l’écriture de récits",
    "Style littéraire et identité de l’auteur",
  ],
};

const CHARACTER_LIMIT = 200000;

const CreatePodcastCard = ({
  onRegenClick,
  onAssignClick,
  openPdfCustomizationModal,
}: {
  onRegenClick: (
    podcastId: number,
    podcastGenerationType: CreatePodcastFromReferenceRequestDtoGenerationType,
  ) => void;
  onAssignClick: (podcastId: number, podcastTitle: string) => void;
  openPdfCustomizationModal?: (podcastId: number | undefined) => void;
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [topic, setTopic] = useState("");
  const [podcastId, setPodcastId] = useState<number | undefined>(undefined);
  const [showNewPodcast, setShowNewPodcast] = useState(false);
  const [latestPodcast, setLatestPodcast] = useState<PodcastResponseDto | null>(
    null,
  );
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [generatorMode, setGeneratorMode] = useState<"TEXT" | "PDF">("TEXT");
  const [uploadedPdfIds, setUploadedPdfIds] = useState<number[]>([]);
  const [uploadError, setUploadError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [outputLanguage, setOutputLanguage] = useState<string>("en");
  const [cefrLevel, setCefrLevel] = useState<CreatePodcastRequestDtoCefrLevel>(
    CreatePodcastRequestDtoCefrLevel.A1,
  );
  const dispatch = useAppDispatch();

  // Theme gradient classes for OutputLanguageSelector
  const gradientClasses = {
    red: "from-red-600 to-red-700",
    orange: "from-orange-600 to-orange-700",
    blue: "from-blue-600 to-blue-700",
    teal: "from-teal-600 to-teal-700",
    cyan: "from-cyan-600 to-cyan-700",
    rose: "from-rose-600 to-rose-700",
    stone: "from-stone-600 to-stone-700",
    lime: "from-lime-600 to-lime-700",
    terra: "from-amber-600 to-orange-700",
    purple: "from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400",
    amber: "from-amber-600 to-amber-700",
    emerald: "from-emerald-600 to-emerald-700",
    violet: "from-violet-600 to-violet-700",
    slate: "from-slate-600 to-slate-700",
    yellow: "from-yellow-600 to-yellow-700",
    fuchsia: "from-fuchsia-600 to-fuchsia-700",
    zinc: "from-zinc-600 to-zinc-700",
    green: "from-green-600 to-green-700",
    coral: "from-pink-600 to-orange-600",
    mint: "from-green-400 to-teal-500",
    lavender: "from-purple-400 to-blue-500",
    sunset: "from-orange-500 to-pink-600",
    ocean: "from-blue-500 to-teal-600",
    bloom: "from-pink-500 to-purple-600",
    cream: "from-yellow-100 to-yellow-200",
  };

  const steps = useMemo(() => {
    if (i18n.language === "tr")
      return [
        "Konu analiz ediliyor ve bilgi toplanıyor...",
        "Görsel yapı oluşturuluyor...",
        "Kavramlar ve bağlantılar düzenleniyor...",
        "Zihin haritası oluşturma işlemi tamamlanıyor...",
        "Ses oluşturma işlemi tamamlanıyor...",
      ];
    if (i18n.language === "fr")
      return [
        "Analyse du sujet et collecte d'informations...",
        "Création de la structure visuelle...",
        "Organisation des concepts et connexions...",
        "Finalisation de la génération de la carte mentale...",
        "Génération de l'audio...",
      ];
    if (i18n.language === "de")
      return [
        "Thema analysieren und Informationen sammeln...",
        "Visuelle Struktur erstellen...",
        "Konzepte und Verbindungen organisieren...",
        "Mind Map-Generierung abschließen...",
        "Audio generieren...",
      ];
    return [
      "Analyzing topic and gathering information...",
      "Creating visual structure...",
      "Organizing concepts and connections...",
      "Finalizing mind map generation...",
      "Generating audio...",
    ];
  }, [i18n.language]);

  const randomExampleTopics = useMemo(() => {
    const topics =
      exampleTopics[i18n.language as "tr" | "en" | "de"] || exampleTopics["en"];
    return topics.sort(() => Math.random() - 0.5).slice(0, 8);
  }, [i18n.language]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    if (generatorMode === "TEXT") {
      return topic?.trim().length > 0;
    } else {
      return uploadedPdfIds.length > 0;
    }
  }, [generatorMode, topic, uploadedPdfIds]);

  const { data: podcast } = useGetPodcast(podcastId!, {
    query: {
      enabled: podcastId !== undefined,

      refetchInterval: (query) => {
        if (query?.state?.data?.status === "FAILED") {
          return false;
        }
        if (!query?.state?.data?.transcript || !query?.state?.data?.url) {
          return 5000;
        }
        if (!query?.state?.data?.imageUrl) {
          return 3000;
        }

        return false;
      },
      queryFn: async () => {
        const response = await getPodcast(podcastId!);
        if (response?.imageUrl) {
          setIsImageLoading(false);
        } else {
          setIsImageLoading(true);
        }

        if (response?.status === "FAILED") {
          setPodcastId(undefined);
          setTopic("");
          setUploadedPdfIds([]);

          addToast({
            title: t("podcast.errors.generationFailed"),
            description: t("podcast.errors.tryAgain"),
            variant: "error",
            duration: 5000,
          });
        }

        return response;
      },
    },
  });
  useEffect(() => {
    if (podcast?.transcript && podcast?.url) {
      console.log("Podcast data:", podcast);
      console.log("Questions:", podcast.questions);
      setLatestPodcast(podcast);
      setShowNewPodcast(true);
      queryClient.invalidateQueries({
        queryKey: getGetUserPodcastsQueryKey(),
      });
      setPodcastId(undefined);
      setTopic("");
      setUploadedPdfIds([]);
    }
  }, [podcast?.transcript, podcast?.url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trim the topic and check if it's valid
    const trimmedTopic = topic?.trim();

    if (!trimmedTopic && !uploadedPdfIds?.length) return;

    if (generatorMode === "TEXT" && trimmedTopic?.length > CHARACTER_LIMIT) {
      addToast({
        title: t("podcast.errors.tooLong"),
        description: t("podcast.errors.characterLimit", {
          limit: CHARACTER_LIMIT.toLocaleString(),
        }),
        variant: "error",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const podcast = await createPodcast({
        prompt: generatorMode === "TEXT" ? trimmedTopic : undefined,
        environment: getEnvironment(),
        generationType:
          generatorMode === "TEXT"
            ? CreatePodcastRequestDtoGenerationType.PROMPT
            : CreatePodcastRequestDtoGenerationType.TEACHER_DOCUMENT,
        teacherDocumentIds: uploadedPdfIds || undefined,
        contentLanguage:
          outputLanguage as CreatePodcastRequestDtoContentLanguage,
        cefrLevel:
          outputLanguage === "en"
            ? cefrLevel
            : CreatePodcastRequestDtoCefrLevel.A1,
      });
      setPodcastId(podcast.id);
      dispatch(updateLastUsedTools("podcasts"));
      track(
        {
          category: "ContentGeneration",
          action: "podcast.generate",
          label: "Podcast generation click",
          nonInteraction: false,
        },
        {
          location: "/podcasts/generate",
          element: "generate-button",
          podcastGenerationType: generatorMode,
          podcastGenerationPrompt: trimmedTopic,
          podcastGenerationFileIds: uploadedPdfIds.join(","),
        },
      );
    } catch (error: any) {
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        addToast({
          title: t("podcast.errors.generationFailed"),
          description: t("podcast.errors.tryAgain"),
          variant: "error",
          duration: 5000,
        });
      }
      setPodcastId(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPodcast = () => {
    setShowNewPodcast(false);
  };

  const { trackEvent: track } = trackEvent();
  const handleClickPredifined = (example: string) => {
    track(
      {
        category: "ContentPredefined",
        action: "podcast.predefined",
        label: "Podcast predefined click",
        nonInteraction: false,
      },
      {
        location: "/podcast/predefined",
        element: "predefined-button",
      },
    );
    console.log(example);
    setTopic(example);
  };

  if (showNewPodcast) {
    return (
      <div className="bg-white dark:bg-gray-900 backdrop-blur-md rounded-xl p-4 sm:p-8 shadow-lg border border-white/20 dark:border-gray-700/20 relative w-full max-w-7xl mx-auto">
        <div className="animate-fadeIn relative">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
            <Text
              variant="h1"
              className="text-purple-600 dark:text-purple-400 text-lg sm:text-xl md:text-2xl"
            >
              {latestPodcast?.title || "podcast.generateNew"}
            </Text>

            <Button
              onClick={handleNewPodcast}
              data-testid="generate-new-podcast-button"
              bgColor="bg-purple-600 hover:bg-purple-700"
              textColor="text-white"
              size="sm"
              className="sm:text-base w-full sm:w-auto"
              rounded="lg"
            >
              {t("podcast.generateNew")}
            </Button>
          </div>
          <div className="w-full mx-auto xl:w-1/2">
            <PodcastAudioCard
              id={latestPodcast?.id}
              url={latestPodcast?.url}
              imageUrl={latestPodcast?.imageUrl}
              isImageLoading={isImageLoading}
              name={latestPodcast?.title}
              transcript={latestPodcast?.transcript}
              createdAt={latestPodcast?.createdAt}
              author={latestPodcast?.authorName}
              onRegenClick={(podcastId, podcastGenerationType) => {
                if (podcastId) {
                  onRegenClick(podcastId, podcastGenerationType);
                }
              }}
              onAssignClick={(podcastId, podcastTitle) => {
                if (podcastId) {
                  onAssignClick(podcastId, podcastTitle);
                }
              }}
              openPdfCustomizationModal={openPdfCustomizationModal}
              podcastGenerationType={
                latestPodcast?.teacherDocuments &&
                latestPodcast?.teacherDocuments?.length > 0
                  ? CreatePodcastFromReferenceRequestDtoGenerationType.TEACHER_DOCUMENT
                  : CreatePodcastFromReferenceRequestDtoGenerationType.PROMPT
              }
              questions={latestPodcast?.questions as unknown as Question[]}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 backdrop-blur-md rounded-xl p-8 shadow-lg border border-white/20 dark:border-gray-700/20 relative">
      <div className="relative generate-podcast-form">
        <div className="space-y-4 sm:space-y-8">
          {podcastId === undefined && (
            <div className="space-y-4">
              <Text
                variant="h3"
                className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400"
              >
                {t("podcast.selectInputType")}
              </Text>

              <div className="grid grid-cols-2">
                <Button
                  onClick={() => setGeneratorMode("TEXT")}
                  bgColor={
                    generatorMode === "TEXT"
                      ? "bg-purple-50 dark:bg-dark-900"
                      : "dark:bg-dark-500"
                  }
                  className={`p-4 border rounded-l-xl ${
                    generatorMode === "TEXT"
                      ? "border-dark-400"
                      : "border-dark-400"
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Sparkles className="w-5 h-5" />
                    <span>{t("podcast.textInput")}</span>
                  </div>
                </Button>

                <Button
                  onClick={() => setGeneratorMode("PDF")}
                  bgColor={
                    generatorMode === "PDF"
                      ? "bg-purple-50 dark:bg-dark-900"
                      : "dark:bg-dark-500"
                  }
                  className={`p-4 border rounded-r-xl ${
                    generatorMode === "PDF"
                      ? "border-dark-400"
                      : "border-dark-400"
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span>{t("podcast.pdfInput")}</span>
                  </div>
                </Button>
              </div>

              {generatorMode === "TEXT" ? (
                <div className="space-y-2">
                  {/* Topic input on top */}
                  <div className="flex-1">
                    <Text
                      variant="h4"
                      className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-2"
                    >
                      {t("transferActivities.form.topic")}
                    </Text>
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder={t("podcast.placeholder")}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400 shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    />
                  </div>
                  {/* Output language and CEFR selector below, in a row */}
                  <div className="flex flex-wrap gap-2 topic-suggestion">
                    {randomExampleTopics.map((example) => (
                      <Button
                        key={example}
                        type="button"
                        onClick={() => handleClickPredifined(example)}
                        data-testid="topic-suggestion"
                        variant="solid"
                        size="sm"
                        rounded="full"
                        textColor="text-gray-700 dark:text-gray-300"
                        bgColor="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {example}
                      </Button>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <div className="flex flex-col sm:w-48 mt-4 hidden">
                      <Text
                        variant="h4"
                        className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-2"
                      >
                        {t("hooks.form.outputLanguage")}
                      </Text>
                      <OutputLanguageSelector
                        selectedLanguage={outputLanguage}
                        onLanguageChange={setOutputLanguage}
                        themeColor="purple"
                        gradientClasses={gradientClasses}
                      />
                    </div>
                    {outputLanguage === "en" && (
                      <div className="flex flex-col w-full mt-4">
                        <Text
                          variant="h4"
                          className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-2"
                        >
                          {t("podcast.cefrLevel")}
                        </Text>
                        <CEFRSelector
                          selectedLevel={cefrLevel}
                          onLevelChange={setCefrLevel}
                          themeColor="purple"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <FileUploader
                    onUploadSuccess={(_, response) => {
                      setUploadedPdfIds([response?.id!]);
                      setTopic(
                        response?.fileName?.replace(/\.[^/.]+$/, "") ?? "",
                      );
                    }}
                    onFileDelete={() => {
                      setUploadedPdfIds([]);
                      setTopic("");
                    }}
                    onUploadError={(error) => setUploadError(error)}
                  />
                  {uploadError && (
                    <Text variant="p" status="error" size="sm">
                      {uploadError}
                    </Text>
                  )}

                  <div className="flex flex-col lg:flex-row gap-4 items-center">
                    {/* Output Language Selector for PDF mode */}
                    <div className="flex flex-col lg:w-48 hidden">
                      <Text
                        variant="h4"
                        className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-2"
                      >
                        {t("hooks.form.outputLanguage")}
                      </Text>
                      <OutputLanguageSelector
                        selectedLanguage={outputLanguage}
                        onLanguageChange={setOutputLanguage}
                        themeColor="purple"
                        gradientClasses={gradientClasses}
                      />
                    </div>

                    {/* CEFR Level Selector for PDF mode - Only show for English */}
                    {outputLanguage === "en" && (
                      <div className="flex flex-col w-full">
                        <Text
                          variant="h4"
                          className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-2"
                        >
                          {t("podcast.cefrLevel")}
                        </Text>
                        <CEFRSelector
                          selectedLevel={cefrLevel}
                          onLevelChange={setCefrLevel}
                          themeColor="purple"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <Button
            variant="solid"
            data-testid="generate-podcast-button"
            size="lg"
            rounded="xl"
            fullWidth
            bgColor="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            textColor="text-white"
            className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-purple-500/50"
            disabled={!isFormValid || podcastId !== undefined || isLoading}
            loading={isLoading}
            onClick={handleSubmit}
          >
            {podcastId !== undefined
              ? t("podcast.generating")
              : t("podcast.generateAudioLesson")}
          </Button>

          {podcastId !== undefined && (
            <LoadingContainer
              showSteps={steps}
              title={t("podcast.generating")}
              themeColor="purple"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePodcastCard;
